import type { Country } from './types';

/**
 * Oceania countries with full metadata.
 */
export const oceaniaCountries: Country[] = [
  {
    code: 'AU',
    name: 'Australia',
    flag: '\u{1F1E6}\u{1F1FA}',
    continent: 'Oceania',
    subregion: 'Australasia',
    details: {
      capital: 'Canberra',
      population: '26.0M',
      currency: 'AUD ($)',
      languages: 'English',
      area: '7,692,024 km\u00B2',
    },
    mapHints: {
      // Australia has external territories (Christmas Island, Cocos Islands, etc.)
      // Show mainland + Tasmania, exclude very distant territories
      fitStrategy: 'auto',
      maxTerritoryDistance: 1000,
    },
  },
  {
    code: 'NZ',
    name: 'New Zealand',
    flag: '\u{1F1F3}\u{1F1FF}',
    continent: 'Oceania',
    subregion: 'Australasia',
    details: {
      capital: 'Wellington',
      population: '5.1M',
      currency: 'NZD ($)',
      languages: 'English, M\u0101ori',
      area: '268,838 km\u00B2',
    },
    mapHints: {
      // New Zealand: North Island + South Island (close together)
      // Exclude Tokelau, Cook Islands, etc.
      fitStrategy: 'auto',
      maxTerritoryDistance: 800,
    },
  },
  {
    code: 'FJ',
    name: 'Fiji',
    flag: '\u{1F1EB}\u{1F1EF}',
    continent: 'Oceania',
    subregion: 'Melanesia',
    details: {
      capital: 'Suva',
      population: '924K',
      currency: 'FJD ($)',
      languages: 'English, Fijian, Hindi',
      area: '18,274 km\u00B2',
    },
    mapHints: {
      fitStrategy: 'full',
    },
  },
  {
    code: 'PG',
    name: 'Papua New Guinea',
    flag: '\u{1F1F5}\u{1F1EC}',
    continent: 'Oceania',
    subregion: 'Melanesia',
    details: {
      capital: 'Port Moresby',
      population: '9.1M',
      currency: 'PGK (K)',
      languages: 'English, Tok Pisin, Hiri Motu',
      area: '462,840 km\u00B2',
    },
    mapHints: {
      fitStrategy: 'full',
    },
  },
  {
    code: 'WS',
    name: 'Samoa',
    flag: '\u{1F1FC}\u{1F1F8}',
    continent: 'Oceania',
    subregion: 'Polynesia',
    details: {
      capital: 'Apia',
      population: '200K',
      currency: 'WST (T)',
      languages: 'Samoan, English',
      area: '2,842 km\u00B2',
    },
  },
  {
    code: 'TO',
    name: 'Tonga',
    flag: '\u{1F1F9}\u{1F1F4}',
    continent: 'Oceania',
    subregion: 'Polynesia',
    details: {
      capital: "Nuku'alofa",
      population: '106K',
      currency: 'TOP (T$)',
      languages: 'Tongan, English',
      area: '747 km\u00B2',
    },
  },
  {
    code: 'VU',
    name: 'Vanuatu',
    flag: '\u{1F1FB}\u{1F1FA}',
    continent: 'Oceania',
    subregion: 'Melanesia',
    details: {
      capital: 'Port Vila',
      population: '319K',
      currency: 'VUV (Vt)',
      languages: 'Bislama, English, French',
      area: '12,189 km\u00B2',
    },
  },
  {
    code: 'SB',
    name: 'Solomon Islands',
    flag: '\u{1F1F8}\u{1F1E7}',
    continent: 'Oceania',
    subregion: 'Melanesia',
    details: {
      capital: 'Honiara',
      population: '721K',
      currency: 'SBD ($)',
      languages: 'English',
      area: '28,896 km\u00B2',
    },
    mapHints: {
      nameInAtlas: 'Solomon Is.',
    },
  },
  {
    code: 'KI',
    name: 'Kiribati',
    flag: '\u{1F1F0}\u{1F1EE}',
    continent: 'Oceania',
    subregion: 'Micronesia',
    details: {
      capital: 'Tarawa',
      population: '121K',
      currency: 'AUD ($)',
      languages: 'Gilbertese, English',
      area: '811 km\u00B2',
    },
  },
  {
    code: 'FM',
    name: 'Micronesia',
    officialName: 'Federated States of Micronesia',
    flag: '\u{1F1EB}\u{1F1F2}',
    continent: 'Oceania',
    subregion: 'Micronesia',
    details: {
      capital: 'Palikir',
      population: '114K',
      currency: 'USD ($)',
      languages: 'English',
      area: '702 km\u00B2',
    },
  },
  {
    code: 'PW',
    name: 'Palau',
    flag: '\u{1F1F5}\u{1F1FC}',
    continent: 'Oceania',
    subregion: 'Micronesia',
    details: {
      capital: 'Ngerulmud',
      population: '18K',
      currency: 'USD ($)',
      languages: 'Palauan, English',
      area: '459 km\u00B2',
    },
  },
  {
    code: 'MH',
    name: 'Marshall Islands',
    flag: '\u{1F1F2}\u{1F1ED}',
    continent: 'Oceania',
    subregion: 'Micronesia',
    details: {
      capital: 'Majuro',
      population: '59K',
      currency: 'USD ($)',
      languages: 'Marshallese, English',
      area: '181 km\u00B2',
    },
    mapHints: {
      nameInAtlas: 'Marshall Is.',
    },
  },
  {
    code: 'NR',
    name: 'Nauru',
    flag: '\u{1F1F3}\u{1F1F7}',
    continent: 'Oceania',
    subregion: 'Micronesia',
    details: {
      capital: 'Yaren',
      population: '11K',
      currency: 'AUD ($)',
      languages: 'Nauruan, English',
      area: '21 km\u00B2',
    },
  },
  {
    code: 'TV',
    name: 'Tuvalu',
    flag: '\u{1F1F9}\u{1F1FB}',
    continent: 'Oceania',
    subregion: 'Polynesia',
    details: {
      capital: 'Funafuti',
      population: '12K',
      currency: 'AUD ($)',
      languages: 'Tuvaluan, English',
      area: '26 km\u00B2',
    },
  },
  {
    code: 'NC',
    name: 'New Caledonia',
    flag: '\u{1F1F3}\u{1F1E8}',
    continent: 'Oceania',
    subregion: 'Melanesia',
    details: {
      capital: 'Noum\u00E9a',
      population: '288K',
      currency: 'XPF (Fr)',
      languages: 'French',
      area: '18,575 km\u00B2',
    },
  },
  {
    code: 'PF',
    name: 'French Polynesia',
    flag: '\u{1F1F5}\u{1F1EB}',
    continent: 'Oceania',
    subregion: 'Polynesia',
    details: {
      capital: 'Papeete',
      population: '280K',
      currency: 'XPF (Fr)',
      languages: 'French, Tahitian',
      area: '4,167 km\u00B2',
    },
  },
];
